/* ===================================================================
   useCustomerMutations() -- add a customer + bulk CSV import (Hopper,
   2026-06-17).

   add()    -> POST /api/v1/customers          (one upserted contact)
   import() -> POST /api/v1/customers/import    (bulk CSV rows)

   Both persist to public.customers scoped to the signed-in client's
   rr_ token (the server stamps client_id from the token -- a caller can
   never write into another tenant). After either succeeds we invalidate
   the customers query so the table refetches from the DB (no local mock
   state). Fires ONLY on an explicit operator click; nothing here sends
   any SMS/email.
   =================================================================== */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CustomerCreated,
  type CustomerImportResult,
  type CustomerInput,
} from '../lib/api';
import { qk } from './queryKeys';

export interface UseCustomerMutationsResult {
  add: (input: CustomerInput) => Promise<CustomerCreated>;
  importRows: (rows: CustomerInput[]) => Promise<CustomerImportResult>;
  /* Mark a customer do-not-contact (true) or restore them (false). Permanent
     suppression: a true customer is excluded from ALL future review requests. */
  setDoNotContact: (customerId: string, doNotContact: boolean) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
}

export function useCustomerMutations(): UseCustomerMutationsResult {
  const qc = useQueryClient();

  // Refresh the customer list (+ statline counts) after a write.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.customers() });
  };

  const addM = useMutation({
    mutationFn: (input: CustomerInput) => api.createCustomer(input),
    onSuccess: invalidate,
  });
  const importM = useMutation({
    mutationFn: (rows: CustomerInput[]) => api.importCustomers(rows),
    onSuccess: invalidate,
  });
  const dncM = useMutation({
    mutationFn: (vars: { customerId: string; doNotContact: boolean }) =>
      api.setCustomerDoNotContact(vars.customerId, vars.doNotContact),
    onSuccess: invalidate,
  });

  return {
    add: (input) => addM.mutateAsync(input),
    importRows: (rows) => importM.mutateAsync(rows),
    setDoNotContact: (customerId, doNotContact) =>
      dncM.mutateAsync({ customerId, doNotContact }),
    isPending: addM.isPending || importM.isPending || dncM.isPending,
    error: addM.error ?? importM.error ?? dncM.error ?? null,
  };
}
